import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DividerModule } from '@openng/optimus-ui/divider';
import {
  DialogService,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';
import {
  CommercialFacility,
  CommercialFacilityNote,
} from 'src/app/model/commercial-services/commercial-facility';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { sortByProperty, SortDirection } from 'src/app/utils/utils';
import {
  CommercialFacilityNoteDialogComponent,
  CommercialFacilityNoteDialogData,
} from '../commercial-facility-note-dialog/commercial-facility-note-dialog.component';

@Component({
  selector: 'app-commercial-facility-notepad',
  imports: [ButtonModule, DividerModule, LogEntryComponent, TranslateModule],
  templateUrl: './commercial-facility-notepad.component.html',
  styleUrl: './commercial-facility-notepad.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommercialFacilityNotepadComponent implements OnDestroy {
  private readonly _commercialFacilityApi = inject(
    CommercialFacilityApiService
  );
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _dialogService = inject(DialogService);
  private readonly _logAndMessage = inject(LogAndMessageService);
  private readonly _translateService = inject(TranslateService);
  private entryDialog: DynamicDialogRef | null = null;

  readonly facility = input<CommercialFacility | null>(null);
  readonly writeAccess = input(false);
  readonly notesChanged = output();

  readonly notes = computed(() =>
    [...(this.facility()?.Notes ?? [])].sort((a, b) =>
      sortByProperty(a, b, 'time', SortDirection.DESCENDING)
    )
  );

  ngOnDestroy(): void {
    this.entryDialog?.close();
  }

  showEntryForm(note: CommercialFacilityNote | null = null): void {
    const facility = this.facility();
    if (!facility?.id || !this.writeAccess() || this.entryDialog) {
      return;
    }

    const data: CommercialFacilityNoteDialogData = {
      facilityId: facility.id,
      note,
      writeAccess: this.writeAccess,
    };
    this.entryDialog = this._dialogService.open(
      CommercialFacilityNoteDialogComponent,
      {
        header: this._translateService.instant(
          note
            ? 'COMMERCIAL_SERVICES.LABEL.EDIT_NOTE_HEADER'
            : 'COMMERCIAL_SERVICES.LABEL.ADD_NOTE_HEADER'
        ),
        modal: true,
        closable: true,
        closeOnEscape: true,
        width: '1000px',
        transitionOptions: '0ms',
        data,
      }
    );
    this.entryDialog?.onClose.subscribe((saved?: boolean) => {
      this.entryDialog = null;
      if (saved) {
        this.notesChanged.emit();
      }
    });
  }

  deleteEntry(note: CommercialFacilityNote): void {
    if (!this.writeAccess()) {
      return;
    }

    this._confirmationService.confirm({
      header: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.HEADERS.DELETE_NOTE'
      ),
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this._translateService.instant('COMMON.LABEL.BUTTONS.OK'),
      rejectLabel: this._translateService.instant(
        'COMMON.LABEL.BUTTONS.CANCEL'
      ),
      message: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.CONFIRMATION.DELETE_NOTE'
      ),
      accept: () => {
        this._commercialFacilityApi
          .deleteCommercialFacilityNote(note.id)
          .subscribe({
            next: () => this.notesChanged.emit(),
            error: (error) => {
              console.error(error);
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                bodyKey:
                  'COMMERCIAL_SERVICES.MESSAGES.ERROR.DELETE_COMMERCIAL_FACILITY_NOTE',
              });
            },
          });
      },
    });
  }
}
